import { HttpMethod, HttpMethodType, IApiResponse, IHttpResponse } from '@aph/common-react';
import queryString from 'query-string';

/**
 * GetEnvironmentVariable
 *
 * @description Helper method to get an environment variable
 * @param {string} name Variable name
 * @returns {string} Variable value
 */
export const getEnvironmentVariable = (name: string): string | undefined => {
  const variable = import.meta.env.PROD ? (window as any).env[name] : import.meta.env[`VITE_${name}`];

  if (!variable) {
    // eslint-disable-next-line no-console
    console.warn(`Environment variable '${name}' is not configured`);
  }

  return variable;
};

/**
 * UpdateUrlSearchParameters
 *
 * @description
 * @param {string}                 key   Parameter key
 * @param {string | Array<string>} value Parameter value
 */
export const updateUrlSearchParameters = (key: string, value: string | Array<string | null> | null): void => {
  const parsedQueryParameters = queryString.parse(location.search);

  if (value === null) {
    delete parsedQueryParameters[key];
    window.history.replaceState(null, '', `?${queryString.stringify({ ...parsedQueryParameters })}`);
  }

  window.history.replaceState(null, '', `?${queryString.stringify({ ...parsedQueryParameters, [key]: value })}`);
};

/**
 * ParseArrayUrlParameter
 *
 * @description Helper method to handle parsing arrays because currently if it is only a single value then it serialises as a string instead of an array
 * @example Type=bill needs to become ["bill"] and not "bill"
 * @param {queryString.ParsedQuery<string>} parsedQueryParameters Object with the parsed query parameters
 * @param {string}                          selectedPropertyKey   Selected property key
 * @returns {Array<string>} List of values from query parameter as an array
 * @see https://www.npmjs.com/package/query-string This helper relies on parsing using the selected library
 */
export const parseArrayUrlParameter = (parsedQueryParameters: queryString.ParsedQuery<string>, selectedPropertyKey: string): Array<string> => {
  const parsedParameters = parsedQueryParameters[selectedPropertyKey] && typeof(parsedQueryParameters[selectedPropertyKey]) === 'string'
    ? [parsedQueryParameters[selectedPropertyKey] as string]
    : parsedQueryParameters[selectedPropertyKey] as Array<string> ?? [];

  return parsedParameters;
};

/**
 * value to look for in header to serialise response if json
 */
const applicationJsonHeader  = 'application/json';

/**
 * Create Service Instance
 *
 * @description Allows a service to create an instance of Axios
 * @param {string}                  url     Url used for the fetch request
 * @param {HttpMethod}              method  Selected HTTP Method
 * @param {any}                body    Request body of selected type
 * @param {RequestInit | undefined} options Additional request config
 * @returns {Promise<HttpResponse<T>>} Fetch request
 */
export const executeFetchService = async <T extends IApiResponse>(
  url: string,
  method: HttpMethodType,
  body: any = null,
  options?: RequestInit): Promise<IHttpResponse<T>> => {

  // Setting up Fetch config
  const config: RequestInit = {
    method: method,
    headers: {
      'Accept': applicationJsonHeader
    },
    ...options
  };

  if (method !== HttpMethod.GET) {
    config.body = JSON.stringify(body);
  }

  const fetchRequest = new Request(url, config);

  // Execute fetch request and added parsed response
  const response: IHttpResponse<T> = await fetch(fetchRequest);

  /**
   * Checking response for JSON and deserialise
   * as not all responses (eg files) can be deserialised
   */
  try {
    // Use includes, some headers are like 'application/json; charset=utf-8'
    if (response.headers.get('content-type')?.includes(applicationJsonHeader)) {
      // Deserialise fetch body
      response.parsedBody = await response.json();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error attempting to convert API response body to json.', e);
  }

  return response;
};